import React__default, { useState, useEffect, createElement, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMeasure, useClickAway } from 'react-use';
import Select from 'react-select';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _construct(Parent, args, Class) {
  if (_isNativeReflectConstruct()) {
    _construct = Reflect.construct;
  } else {
    _construct = function _construct(Parent, args, Class) {
      var a = [null];
      a.push.apply(a, args);
      var Constructor = Function.bind.apply(Parent, a);
      var instance = new Constructor();
      if (Class) _setPrototypeOf(instance, Class.prototype);
      return instance;
    };
  }

  return _construct.apply(null, arguments);
}

var SlashContainer = function SlashContainer(_ref) {
  var backgroundColor = _ref.backgroundColor,
      children = _ref.children,
      width = _ref.width,
      offset = _ref.offset,
      position = _ref.position;

  var _useState = useState(0),
      slashAngle = _useState[0],
      setSlashAngle = _useState[1];

  var _useMeasure = useMeasure(),
      slashContainerRef = _useMeasure[0],
      _useMeasure$ = _useMeasure[1],
      currentHeight = _useMeasure$.height,
      currentWidth = _useMeasure$.width;

  useEffect(function () {
    if (position === 'top') {
      var angle = Math.atan(offset * 2 / currentWidth) * (180 / Math.PI);
      setSlashAngle(angle);
    } else {
      var _angle = Math.atan(offset * 2 / currentHeight) * (180 / Math.PI);

      setSlashAngle(_angle);
    }
  }, [currentHeight, offset, currentWidth]);

  var getTransform = function getTransform() {
    switch (position) {
      case 'slash':
        return {
          transform: "skewX(-" + slashAngle + "deg)",
          left: offset + "px",
          top: 0
        };

      case 'backslash':
        return {
          transform: "skewX(" + slashAngle + "deg)",
          left: offset + "px",
          top: 0
        };

      case 'top':
        return {
          transform: "skewY(-" + slashAngle + "deg)",
          left: 0,
          top: offset + "px"
        };

      default:
        return {
          transform: "skewX(-" + slashAngle + "deg)",
          left: offset + "px",
          top: 0
        };
    }
  };

  var getContainerStyle = function getContainerStyle() {
    var style = {
      position: 'absolute'
    };

    if (position === 'top') {
      return _extends({}, style, {
        width: '100%',
        height: offset * 2 + "px",
        top: "-" + offset * 2 + "px",
        left: 0
      });
    }

    return _extends({}, style, {
      width: offset * 2 + "px",
      height: currentHeight,
      right: "-" + offset * 2 + "px"
    });
  };

  var renderSlash = function renderSlash() {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "relative d-flex f-row justify-end align-center",
      style: getContainerStyle()
    }, /*#__PURE__*/React__default.createElement("div", {
      className: "full-size absolute",
      style: _extends({
        background: backgroundColor
      }, getTransform())
    }), position !== 'top' ? /*#__PURE__*/React__default.createElement("div", {
      className: "absolute full-height",
      style: {
        width: offset * 2 + "px",
        background: backgroundColor,
        top: 0,
        left: 0
      }
    }) : null);
  };

  return /*#__PURE__*/React__default.createElement("div", {
    className: "relative d-flex",
    style: {
      width: width,
      marginTop: position === 'top' ? offset * 2 + "px" : 0
    }
  }, /*#__PURE__*/React__default.createElement("div", {
    ref: slashContainerRef,
    className: "fgIndex",
    style: {
      background: backgroundColor,
      width: '100%'
    }
  }, children), renderSlash());
};

SlashContainer.propTypes = {
  backgroundColor: PropTypes.string,
  offset: PropTypes.number,
  children: PropTypes.node,
  width: PropTypes.string.isRequired,
  position: PropTypes.oneOf(['slash', 'backslash', 'top'])
};
SlashContainer.defaultProps = {
  backgroundColor: 'white',
  children: /*#__PURE__*/React__default.createElement("div", null),
  offset: 20,
  position: 'slash'
};

var SelectComponent = function SelectComponent(_ref) {
  var data = _ref.data,
      defaultValue = _ref.defaultValue,
      isDisabled = _ref.isDisabled,
      isLoading = _ref.isLoading,
      name = _ref.name,
      isSearchable = _ref.isSearchable,
      value = _ref.value,
      setValue = _ref.setValue,
      placeholder = _ref.placeholder,
      isClearable = _ref.isClearable,
      label = _ref.label,
      theme = _ref.theme;
  var customStyle = {
    indicatorSeparator: function indicatorSeparator() {
      return {
        display: 'none'
      };
    },
    dropdownIndicator: function dropdownIndicator() {
      return {
        color: '#4e5b74',
        margin: '5px',
        cursor: 'pointer'
      };
    },
    control: function control() {
      return {
        backgroundColor: theme === 'dark' ? '#F4F5F9' : '#FFF',
        borderColor: theme === 'dark' ? 'rgba(31,35,44,0.12)' : '#F4F5F9',
        alignItems: 'center',
        borderRadius: '5px',
        borderStyle: 'solid',
        borderWidth: '1px',
        cursor: 'pointer',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        padding: '11px 0'
      };
    },
    singleValue: function singleValue(provided) {
      return _extends({}, provided, {
        color: '#1f232c',
        fontFamily: "'Work Sans', sans-serif",
        fontWeight: 500
      });
    },
    placeholder: function placeholder(provided) {
      return _extends({}, provided, {
        fontFamily: "'Work Sans', sans-serif",
        fontWeight: 500
      });
    },
    option: function option(provided, state) {
      return _extends({}, provided, {
        color: state.isSelected ? '#d01408' : '#1f232c',
        fontFamily: "'Work Sans', sans-serif"
      });
    }
  };

  var customTheme = function customTheme(theme) {
    return _extends({}, theme, {
      colors: _extends({}, theme.colors, {
        primary: '#f4f5f9',
        primary75: '#d01408',
        primary50: '#d01408',
        primary25: '#f4f5f9'
      })
    });
  };

  return /*#__PURE__*/React__default.createElement("div", null, label ? /*#__PURE__*/React__default.createElement("span", {
    className: (theme === 'dark' ? 'grey-400-text' : 'white-text light-label') + " medium-text ml-2"
  }, label) : null, /*#__PURE__*/React__default.createElement(Select, {
    styles: customStyle,
    defaultValue: defaultValue,
    isDisabled: isDisabled,
    isLoading: isLoading,
    isClearable: isClearable,
    isSearchable: isSearchable,
    name: name,
    value: value,
    onChange: setValue,
    options: data,
    placeholder: placeholder,
    theme: customTheme
  }));
};

SelectComponent.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number || PropTypes.string,
    label: PropTypes.string
  })).isRequired,
  value: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.shape({})]),
    label: PropTypes.string
  }),
  defaultValue: PropTypes.string || PropTypes.number,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  name: PropTypes.string,
  isSearchable: PropTypes.bool,
  isClearable: PropTypes.bool,
  setValue: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark'])
};
SelectComponent.defaultProps = {
  defaultValue: null,
  isDisabled: false,
  isLoading: false,
  isSearchable: true,
  placeholder: 'Sélection...',
  label: '',
  theme: 'light'
};

var ButtonComponent = function ButtonComponent(_ref) {
  var children = _ref.children,
      onClick = _ref.onClick,
      theme = _ref.theme,
      type = _ref.type;

  var getClassNames = function getClassNames() {
    switch (theme) {
      case 'primary':
        return 'btn btn-primary';

      case 'outline':
        return 'btn btn-outline';

      case 'outline-transparent':
        return 'btn btn-outline-transparent';

      case 'outline-black':
        return 'btn btn-outline-black';

      case 'black':
        return 'btn btn-black';

      case 'grey':
        return 'btn btn-grey';

      case 'dark-grey':
        return 'btn btn-dark-grey';

      case 'white':
        return 'btn btn-white';

      default:
        return 'btn btn-primary';
    }
  };

  return /*#__PURE__*/React__default.createElement("button", {
    type: type,
    className: getClassNames(),
    onClick: onClick
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "fgIndex"
  }, " ", children, " "));
};

ButtonComponent.propTypes = {
  onClick: PropTypes.func.isRequired,
  type: PropTypes.string,
  theme: PropTypes.oneOf(['primary', 'outline', 'outline-transparent', 'black', 'outline-black', 'grey', 'white', 'dark-grey'])
};
ButtonComponent.defaultProps = {
  type: 'button',
  theme: 'primary'
};

var _defs, _g;

function _extends$1() {
  _extends$1 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$1.apply(this, arguments);
}

function SvgEyeOff(props) {
  return /*#__PURE__*/createElement("svg", _extends$1({
    width: 24,
    height: 24,
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _defs || (_defs = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("filter", {
    colorInterpolationFilters: "auto",
    id: "eye-off_svg__a"
  }, /*#__PURE__*/createElement("feColorMatrix", {
    "in": "SourceGraphic",
    values: "0 0 0 0 0.533502 0 0 0 0 0.565219 0 0 0 0 0.631824 0 0 0 1.000000 0"
  })))), _g || (_g = /*#__PURE__*/createElement("g", {
    transform: "translate(-658 -1997)",
    filter: "url(#eye-off_svg__a)",
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/createElement("path", {
    d: "M658 1997h24v24h-24z"
  }), /*#__PURE__*/createElement("path", {
    d: "M675.882 2016.297A10.949 10.949 0 01670 2018c-5.392 0-9.878-3.88-10.819-9a10.982 10.982 0 013.34-6.066l-3.129-3.126 1.415-1.415 19.799 19.8-1.415 1.414-3.31-3.31h.001zm-11.947-11.947a8.965 8.965 0 00-2.712 4.65 9.005 9.005 0 0013.201 5.838l-2.028-2.028a4.5 4.5 0 01-6.206-6.206l-2.255-2.254zm6.979 6.978l-3.242-3.242a2.5 2.5 0 003.241 3.241l.001.001zm7.893 2.264l-1.431-1.43a8.935 8.935 0 001.401-3.162 9.005 9.005 0 00-11.225-6.662l-1.578-1.578c1.247-.49 2.606-.76 4.026-.76 5.392 0 9.878 3.88 10.819 9a10.947 10.947 0 01-2.012 4.592zm-9.084-9.084a4.5 4.5 0 014.769 4.769l-4.77-4.769h.001z",
    fill: "#000",
    fillRule: "nonzero"
  }))));
}

var _defs$1, _g$1;

function _extends$2() {
  _extends$2 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$2.apply(this, arguments);
}

function SvgEyeOn(props) {
  return /*#__PURE__*/createElement("svg", _extends$2({
    width: 24,
    height: 24,
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _defs$1 || (_defs$1 = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("filter", {
    colorInterpolationFilters: "auto",
    id: "eye-on_svg__a"
  }, /*#__PURE__*/createElement("feColorMatrix", {
    "in": "SourceGraphic",
    values: "0 0 0 0 0.533502 0 0 0 0 0.565219 0 0 0 0 0.631824 0 0 0 1.000000 0"
  })))), _g$1 || (_g$1 = /*#__PURE__*/createElement("g", {
    transform: "translate(-705 -1999)",
    filter: "url(#eye-on_svg__a)",
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/createElement("path", {
    d: "M705 1999h24v24h-24z"
  }), /*#__PURE__*/createElement("path", {
    d: "M717 2002c5.392 0 9.878 3.88 10.819 9-.94 5.12-5.427 9-10.819 9-5.392 0-9.878-3.88-10.819-9 .94-5.12 5.427-9 10.819-9zm0 16c4.2 0 7.842-2.905 8.777-7a9.005 9.005 0 00-17.554 0 9.005 9.005 0 008.777 7zm0-2.5a4.5 4.5 0 110-9 4.5 4.5 0 010 9zm0-2a2.5 2.5 0 100-5 2.5 2.5 0 000 5z",
    fill: "#000",
    fillRule: "nonzero"
  }))));
}

var InputComponent = function InputComponent(_ref) {
  var value = _ref.value,
      _onChange = _ref.onChange,
      label = _ref.label,
      type = _ref.type,
      id = _ref.id,
      name = _ref.name,
      errorMessage = _ref.errorMessage,
      isError = _ref.isError,
      onBlur = _ref.onBlur,
      disabled = _ref.disabled,
      placeholder = _ref.placeholder;

  var _useState = useState(type),
      localType = _useState[0],
      setLocalType = _useState[1];

  var _useState2 = useState('off'),
      eyeImg = _useState2[0],
      setEyeImg = _useState2[1];

  var _useMeasure = useMeasure(),
      labelRef = _useMeasure[0],
      labelWidth = _useMeasure[1].width;

  var _useMeasure2 = useMeasure(),
      imgRef = _useMeasure2[0],
      imgWidth = _useMeasure2[1].width;

  var _useMeasure3 = useMeasure(),
      containerRef = _useMeasure3[0],
      containerWidth = _useMeasure3[1].width;

  var togglePassword = function togglePassword(e) {
    e.stopPropagation();
    var newImg = eyeImg === 'off' ? 'on' : 'off';
    var newType = localType === 'password' ? 'text' : 'password';
    setEyeImg(newImg);
    setLocalType(newType);
  };

  var getInputWidth = function getInputWidth() {
    return containerWidth - (labelWidth + imgWidth + 30);
  };

  return /*#__PURE__*/React__default.createElement("div", {
    ref: containerRef,
    className: "d-flex f-column"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "d-flex " + (getInputWidth() < 30 ? 'f-column' : 'f-row') + " align-center justify-between grey-100-background input-container p-3 radius-5",
    style: {
      opacity: disabled ? 0.5 : 1,
      borderColor: isError ? '#e13c32' : 'rgba(31,35,44,0.12)'
    }
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "d-flex " + (getInputWidth() < 30 ? 'f-column full-width' : 'f-row')
  }, /*#__PURE__*/React__default.createElement("label", {
    className: "input-label w-fit-content",
    ref: labelRef,
    htmlFor: id
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "mr-2 regular-text",
    style: {
      color: value ? '#8890a1' : '#1f232c'
    }
  }, label)), /*#__PURE__*/React__default.createElement("input", {
    style: {
      width: getInputWidth() < 30 ? containerWidth - 30 : getInputWidth()
    },
    type: localType,
    id: id,
    name: name || id,
    value: value,
    onChange: function onChange(e) {
      return _onChange(e.target.value);
    },
    onWheel: function onWheel(event) {
      return event.currentTarget.blur();
    },
    onBlur: onBlur,
    disabled: disabled,
    placeholder: placeholder
  })), type === 'password' ? /*#__PURE__*/React__default.createElement("div", {
    ref: imgRef,
    className: "d-flex align-center",
    onClick: togglePassword
  }, eyeImg === 'on' ? /*#__PURE__*/React__default.createElement(SvgEyeOn, null) : /*#__PURE__*/React__default.createElement(SvgEyeOff, null)) : null), isError ? /*#__PURE__*/React__default.createElement("div", {
    className: "ml-1"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "light-red-text medium-weight regular-text"
  }, errorMessage)) : null);
};

InputComponent.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  label: PropTypes.string,
  type: PropTypes.string,
  errorMessage: PropTypes.string,
  isError: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string
};
InputComponent.defaultProps = {
  label: '',
  type: 'text',
  name: '',
  errorMessage: 'Error',
  isError: false,
  onBlur: function onBlur() {},
  disabled: false,
  placeholder: '',
  value: ''
};

var _path;

function _extends$3() {
  _extends$3 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$3.apply(this, arguments);
}

function SvgUpload(props) {
  return /*#__PURE__*/createElement("svg", _extends$3({
    width: 24,
    height: 24,
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path || (_path = /*#__PURE__*/createElement("path", {
    d: "M3 19h18v2H3v-2zM13 5.828l6.071 6.072 1.414-1.414L12 2l-8.485 8.485L4.929 11.9 11 5.83V17h2V5.828z",
    fill: "#000",
    fillRule: "evenodd"
  })));
}

var _path$1;

function _extends$4() {
  _extends$4 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$4.apply(this, arguments);
}

function SvgCheckboxCircle(props) {
  return /*#__PURE__*/createElement("svg", _extends$4({
    width: 24,
    height: 24,
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1 || (_path$1 = /*#__PURE__*/createElement("path", {
    d: "M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 100-16 8 8 0 000 16zm-.997-4L6.76 11.757l1.414-1.414 2.829 2.829 5.656-5.657 1.415 1.414L11.003 16z",
    fill: "#000",
    fillRule: "nonzero"
  })));
}

var InputFileComponent = function InputFileComponent(_ref) {
  var file = _ref.file,
      setFile = _ref.setFile,
      label = _ref.label,
      initName = _ref.initName,
      isError = _ref.isError,
      errorMessage = _ref.errorMessage,
      disabled = _ref.disabled,
      accept = _ref.accept,
      maxSize = _ref.maxSize;

  var _useState = useState(''),
      fileName = _useState[0],
      setFileName = _useState[1];

  var _useState2 = useState(false),
      isTooBig = _useState2[0],
      setIsTooBig = _useState2[1];

  var fileInputRef = useRef();

  var handleFileInputClick = function handleFileInputClick() {
    fileInputRef.current.click();
  };

  var isSizeOk = function isSizeOk(file) {
    if (maxSize) {
      return file.size <= maxSize;
    }

    return true;
  };

  return /*#__PURE__*/React__default.createElement("div", {
    className: "l-margin-vertical d-flex f-column",
    style: {
      opacity: disabled ? 0.5 : 1
    }
  }, /*#__PURE__*/React__default.createElement("input", {
    type: "file",
    ref: fileInputRef,
    value: fileName,
    disabled: disabled,
    className: "custom-file-input s-margin-right",
    onChange: function onChange(e) {
      if (isSizeOk(e.target.files[0])) {
        setIsTooBig(false);
        setFile(e.target.files[0]);
      } else {
        setIsTooBig(true);
      }
    },
    accept: accept
  }), !file ? /*#__PURE__*/React__default.createElement(ButtonComponent, {
    onClick: handleFileInputClick,
    theme: "outline"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "d-flex align-center justify-center full-width button-file-container"
  }, /*#__PURE__*/React__default.createElement(SvgUpload, {
    className: "mr-2 button-file-icon",
    alt: "",
    width: 24,
    height: 24
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "regular-text medium-weight button-file-name"
  }, label))) : /*#__PURE__*/React__default.createElement(ButtonComponent, {
    onClick: function onClick() {
      setFile(undefined);
      setFileName('');
    },
    theme: "outline"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "d-flex align-center justify-center full-width button-file-container"
  }, /*#__PURE__*/React__default.createElement(SvgCheckboxCircle, {
    className: "mr-2 button-file-icon",
    alt: "",
    width: 24,
    height: 24
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "regular-text medium-weight t-overflow-ellipsis button-file-name"
  }, file.name || initName))), isError ? /*#__PURE__*/React__default.createElement("div", {
    className: "ml-1"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "light-red-text medium-weight regular-text"
  }, errorMessage)) : null, isTooBig ? /*#__PURE__*/React__default.createElement("div", {
    className: "ml-1"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "light-red-text medium-weight regular-text"
  }, "Le fichier ne doit pas d\xE9passer " + maxSize / 1000 + "Ko")) : null);
};

InputFileComponent.propTypes = {
  file: PropTypes.shape({}),
  setFile: PropTypes.func.isRequired,
  label: PropTypes.string,
  initName: PropTypes.string,
  errorMessage: PropTypes.string,
  isError: PropTypes.bool,
  disabled: PropTypes.bool,
  accept: PropTypes.string,
  maxSize: PropTypes.number
};
InputFileComponent.defaultProps = {
  label: 'Choisir un fichier...',
  errorMessage: 'Error',
  initName: '',
  isError: false,
  disabled: false,
  accept: '*',
  maxSize: null,
  file: null
};

var LoaderComponent = function LoaderComponent(_ref) {
  var size = _ref.size,
      color = _ref.color,
      borderWidth = _ref.borderWidth;
  return /*#__PURE__*/React__default.createElement("div", {
    className: "loader",
    style: {
      width: size,
      height: size,
      border: borderWidth + "px solid transparent",
      borderTop: borderWidth + "px solid " + color,
      borderRight: borderWidth + "px solid " + color,
      borderBottom: borderWidth + "px solid " + color
    }
  });
};

LoaderComponent.propTypes = {
  size: PropTypes.number.isRequired,
  borderWidth: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired
};

var TextAreaComponent = function TextAreaComponent(_ref) {
  var value = _ref.value,
      _onChange = _ref.onChange,
      label = _ref.label,
      id = _ref.id,
      errorMessage = _ref.errorMessage,
      isError = _ref.isError,
      _onBlur = _ref.onBlur,
      disabled = _ref.disabled,
      rows = _ref.rows,
      placeholder = _ref.placeholder;

  var _useMeasure = useMeasure(),
      containerRef = _useMeasure[0],
      containerWidth = _useMeasure[1].width;

  return /*#__PURE__*/React__default.createElement("div", {
    ref: containerRef,
    className: "d-flex f-column"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "d-flex f-column align-center justify-between grey-100-background input-container p-3 radius-5",
    style: {
      opacity: disabled ? 0.5 : 1,
      borderColor: isError ? '#e13c32' : 'rgba(31,35,44,0.12)'
    }
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "d-flex f-column full-width"
  }, /*#__PURE__*/React__default.createElement("label", {
    className: "input-label",
    htmlFor: id
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "mr-2 regular-text",
    style: {
      color: value ? '#8890a1' : '#1f232c'
    }
  }, label)), /*#__PURE__*/React__default.createElement("textarea", {
    style: {
      width: containerWidth - 30,
      resize: 'vertical'
    },
    id: id,
    value: value,
    onChange: function onChange(e) {
      return _onChange(e.target.value);
    },
    onBlur: function onBlur() {
      _onBlur();
    },
    disabled: disabled,
    rows: rows,
    placeholder: placeholder
  }))), isError ? /*#__PURE__*/React__default.createElement("div", {
    className: "ml-1"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "light-red-text medium-weight regular-text"
  }, errorMessage)) : null);
};

TextAreaComponent.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  label: PropTypes.string,
  errorMessage: PropTypes.string,
  isError: PropTypes.bool,
  disabled: PropTypes.bool,
  rows: PropTypes.number,
  placeholder: PropTypes.string
};
TextAreaComponent.defaultProps = {
  label: '',
  errorMessage: 'Error',
  isError: false,
  onBlur: function onBlur() {},
  disabled: false,
  rows: 5,
  placeholder: ''
};

var ModalTemplate = function ModalTemplate(_ref) {
  var children = _ref.children,
      closeModal = _ref.closeModal,
      title = _ref.title;

  var _useMeasure = useMeasure(),
      containerRef = _useMeasure[0],
      width = _useMeasure[1].width;

  var modalContentRef = useRef();
  useClickAway(modalContentRef, closeModal);
  return /*#__PURE__*/React__default.createElement("div", {
    className: "action-modal"
  }, /*#__PURE__*/React__default.createElement("div", {
    ref: modalContentRef
  }, /*#__PURE__*/React__default.createElement("div", {
    ref: containerRef,
    className: "action-modal-content",
    onClick: function onClick(e) {
      return e.stopPropagation();
    },
    onKeyDown: function onKeyDown(e) {
      return e.stopPropagation();
    }
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "d-flex justify-between p-4 align-start"
  }, /*#__PURE__*/React__default.createElement("h1", {
    className: "white-text medium-weight ph-3"
  }, title), /*#__PURE__*/React__default.createElement("div", {
    role: "button",
    tabIndex: 0,
    onClick: closeModal,
    className: "mh-1 pointer"
  }, /*#__PURE__*/React__default.createElement("h4", {
    className: "white-text normal-weight m-0"
  }, "x"))), /*#__PURE__*/React__default.createElement(SlashContainer, {
    width: "100%",
    backgroundColor: "#fff",
    position: "top",
    offset: 40
  }, children), /*#__PURE__*/React__default.createElement("div", {
    className: "white-background d-flex justify-end"
  }, /*#__PURE__*/React__default.createElement("div", {
    style: {
      width: 0,
      height: 0,
      borderStyle: 'solid',
      borderWidth: "0 0 70px " + width * 0.8 + "px",
      borderColor: 'transparent transparent #d01508 transparent'
    }
  })))));
};

ModalTemplate.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  closeModal: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired
};

var PaginationComponent = function PaginationComponent(_ref) {
  var currentPage = _ref.currentPage,
      setCurrentPage = _ref.setCurrentPage,
      itemsPerPage = _ref.itemsPerPage,
      totalList = _ref.totalList,
      totalPage = _ref.totalPage,
      setCurrentList = _ref.setCurrentList,
      hasNext = _ref.hasNext,
      hideTotal = _ref.hideTotal;
  var pagesTotal = useMemo(function () {
    if (totalPage) {
      return totalPage;
    }

    if (totalList) {
      return Math.ceil(totalList.length / itemsPerPage);
    }

    return 0;
  }, [totalPage, totalList, itemsPerPage]);
  var pages = useMemo(function () {
    return Array.from(Array(pagesTotal).keys()).reduce(function (acc, page, index) {
      if (page === 0) {
        return acc;
      }

      if (index % 3 === 0) {
        return [].concat(acc, [[page]]);
      }

      var lastItem = acc !== null && acc !== void 0 && acc.length ? acc.pop() : acc;
      return [].concat(acc, [[].concat(lastItem, [page])]);
    }, []);
  }, [pagesTotal]);

  var setCurrentItems = function setCurrentItems(list, current) {
    var firstIndex = (current - 1) * itemsPerPage;

    var newList = _construct(Array, list);

    var length = itemsPerPage > newList.length - firstIndex ? newList.length - firstIndex : itemsPerPage;
    setCurrentList(newList.splice(firstIndex, length));
  };

  var goToPage = function goToPage(nb) {
    if (!hideTotal) {
      setCurrentPage(nb);
    } else {
      var canGoNext = nb > currentPage && hasNext;
      var canGoPrevious = nb < currentPage && currentPage > 1;

      if (canGoNext || canGoPrevious) {
        setCurrentPage(nb);
      }
    }

    if (totalList) {
      setCurrentItems(totalList, nb);
    }
  };

  var canShowNextButton = useMemo(function () {
    if (hideTotal) {
      return hasNext;
    }

    return currentPage !== pagesTotal;
  }, [hasNext, currentPage, pagesTotal, hideTotal]);
  useEffect(function () {
    if (totalList) {
      setCurrentItems(totalList, currentPage);
    }
  }, [totalList]);
  return pagesTotal > 1 || hideTotal ? /*#__PURE__*/React__default.createElement("div", {
    className: "d-flex f-row align-center"
  }, currentPage > 1 ? /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    onClick: function onClick() {
      return goToPage(currentPage - 1);
    }
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "primary-red-text medium-weight"
  }, "Pr\xE9c\xE9dent")) : null, !hideTotal ? pages.map(function (pages, index, array) {
    if (pages.includes(currentPage)) {
      return pages.map(function (page, pageIndex) {
        return (
          /*#__PURE__*/
          React__default.createElement("div", {
            key: "page-" + pageIndex
          }, index > 0 && pageIndex === 0 ? /*#__PURE__*/React__default.createElement("button", {
            className: "m-3",
            type: "button",
            onClick: function onClick() {
              return goToPage(1);
            }
          }, /*#__PURE__*/React__default.createElement("span", {
            className: "grey-400-text normal-weight mr-3"
          }, "1"), /*#__PURE__*/React__default.createElement("span", {
            className: "grey-400-text normal-weight ml-3"
          }, "...")) : null, /*#__PURE__*/React__default.createElement("button", {
            type: "button",
            className: (page === currentPage ? 'current-page' : '') + " m-3",
            onClick: function onClick() {
              return goToPage(page);
            }
          }, /*#__PURE__*/React__default.createElement("span", {
            className: "" + (page === currentPage ? 'primary-red-text medium-weight' : 'grey-400-text normal-weight')
          }, page)), pageIndex === 2 && pagesTotal > 3 ? /*#__PURE__*/React__default.createElement("span", {
            className: "grey-400-text normal-weight m-3"
          }, " ... ") : null)
        );
      });
    }

    if (index === array.length - 1) {
      return /*#__PURE__*/React__default.createElement("button", {
        className: "m-3",
        key: "last-page",
        type: "button",
        onClick: function onClick() {
          return goToPage(pages[pages.length - 1]);
        }
      }, /*#__PURE__*/React__default.createElement("span", {
        className: "grey-400-text normal-weight"
      }, pages[pages.length - 1]));
    }

    return null;
  }) : /*#__PURE__*/React__default.createElement("div", null, currentPage > 1 ? /*#__PURE__*/React__default.createElement("span", {
    className: "grey-400-text normal-weight ml-3"
  }, "...") : null, /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    className: "current-page m-3"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "primary-red-text medium-weight"
  }, currentPage)), hasNext ? /*#__PURE__*/React__default.createElement("span", {
    className: "grey-400-text normal-weight mr-3"
  }, "...") : null), canShowNextButton ? /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    onClick: function onClick() {
      return goToPage(currentPage + 1);
    }
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "primary-red-text medium-weight"
  }, "Suivant")) : null) : /*#__PURE__*/React__default.createElement("div", null);
};

PaginationComponent.propTypes = {
  currentPage: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  totalPage: PropTypes.number,
  setCurrentPage: PropTypes.func.isRequired,
  totalList: PropTypes.arrayOf(PropTypes.shape({})),
  setCurrentList: PropTypes.func.isRequired,
  hasNext: PropTypes.bool,
  hideTotal: PropTypes.bool
};
PaginationComponent.defaultProps = {
  totalPage: null,
  totalList: null,
  hasNext: false,
  hideTotal: false
};

var isServer = function isServer() {
  return typeof window === 'undefined';
};

var MasonryComponent = function MasonryComponent(_ref) {
  var children = _ref.children,
      columnWidth = _ref.columnWidth,
      containerWidth = _ref.containerWidth;
  var containerRef = useRef();
  useEffect(function () {}, []);
  useEffect(function () {
    if (containerRef.current) {
      if (!isServer()) {
        import('masonry-layout').then(function (Masonry) {
          new Masonry["default"](containerRef.current, {
            itemSelector: '.grid-item',
            columnWidth: columnWidth,
            horizontalOrder: true
          });
        });
      }
    }
  }, [containerRef, containerWidth, children]);
  return /*#__PURE__*/React__default.createElement("div", {
    className: "grid",
    ref: containerRef
  }, children);
};

MasonryComponent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  columnWidth: PropTypes.number.isRequired,
  containerWidth: PropTypes.number.isRequired
};

var ButtonWithArrow = function ButtonWithArrow(_ref) {
  var onClick = _ref.onClick,
      isSelected = _ref.isSelected,
      title = _ref.title;
  return /*#__PURE__*/React__default.createElement("button", {
    className: "button-with-arrow pv-3 ph-5 radius-5\n                " + (isSelected ? 'active' : ''),
    type: "button",
    onClick: onClick
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "capitalize"
  }, title));
};

ButtonWithArrow.propTypes = {
  onClick: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired
};

var CheckboxComponent = function CheckboxComponent(_ref) {
  var value = _ref.value,
      handleChange = _ref.handleChange,
      label = _ref.label,
      id = _ref.id;
  return /*#__PURE__*/React__default.createElement("label", {
    className: "input-checkbox-container",
    htmlFor: id
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "regular-text"
  }, label), /*#__PURE__*/React__default.createElement("input", {
    className: "input-checkbox",
    id: id,
    type: "checkbox",
    value: value,
    checked: value,
    onChange: function onChange() {
      return handleChange(function (state) {
        return !state;
      });
    }
  }), /*#__PURE__*/React__default.createElement("span", {
    className: "input-checkbox-checkmark"
  }));
};

CheckboxComponent.propTypes = {
  value: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  id: PropTypes.string.isRequired
};

var RadioButtons = function RadioButtons(_ref) {
  var options = _ref.options,
      value = _ref.value,
      setValue = _ref.setValue;
  return options.map(function (_ref2) {
    var id = _ref2.id,
        code = _ref2.code;
    return /*#__PURE__*/React__default.createElement("div", {
      key: "radio-button-" + id,
      className: "mr-4"
    }, /*#__PURE__*/React__default.createElement("label", {
      className: "input-radio-container",
      htmlFor: "radio-type-" + id
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "regular-text"
    }, code), /*#__PURE__*/React__default.createElement("input", {
      className: "input-radio",
      id: "radio-type-" + id,
      type: "radio",
      value: value || '',
      checked: value === id,
      onChange: function onChange() {
        return setValue(id);
      },
      name: "radio-prestation-types"
    }), /*#__PURE__*/React__default.createElement("span", {
      className: "input-radio-checkmark"
    })));
  });
};

RadioButtons.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    code: PropTypes.string
  })).isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setValue: PropTypes.func.isRequired
};
RadioButtons.defaultProps = {
  value: ''
};

export { ButtonComponent, ButtonWithArrow, CheckboxComponent, InputComponent, InputFileComponent, LoaderComponent, MasonryComponent, ModalTemplate, PaginationComponent, RadioButtons, SelectComponent, SlashContainer, TextAreaComponent };
//# sourceMappingURL=index.modern.js.map
